import { useCallback, useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Th, Td, NumberInput } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Receipt } from "lucide-react";

const CODES = ["STANDARD", "REDUCED", "ZERO_RATE"];

export const InputTaxBadge = ({ status, testId }) => {
  const { t } = useI18n();
  const tone = status === "COMPLETE" ? "bg-primary/15 text-primary"
    : status === "PARTIAL" ? "bg-chart-3/20 text-chart-3" : "bg-muted text-muted-foreground";
  return <span className={`rounded-full px-2 py-0.5 text-xs ${tone}`} data-testid={testId}>
    {t(`itx.status.${status}`)}
  </span>;
};

/** Vorsteuer je Position eines Eingangsbelegs erfassen bzw. korrigieren.
 *  Ein Beleg kann mehrere Steuerfälle enthalten (z. B. Lebensmittel 7 %, Fracht 19 %);
 *  belegweit werden daraus nur Summen und der Tax Breakdown gebildet. */
export default function InputTaxDialog({ kind, receiptId, open, onClose, onSaved }) {
  const { t, fmtDate } = useI18n();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [head, setHead] = useState({ supplier_invoice_number: "", document_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(() => {
    if (!open || !receiptId) return;
    setLoaded(false);
    api.get(`/input-tax/${kind}/${receiptId}`).then((r) => {
      setData(r.data);
      setHead({ supplier_invoice_number: r.data.supplier_invoice_number || "",
        document_date: (r.data.document_date || r.data.received_at || new Date().toISOString()).slice(0, 10), reason: "" });
      setRows(r.data.lines.map((l) => ({
        index: l.index,
        tax_code: l.input_tax?.tax_code || "STANDARD",
        net: l.input_tax ? String(l.input_tax.net) : (l.net_from_receipt != null ? String(l.net_from_receipt) : ""),
        captured: !!l.input_tax,
        // Nur ausdrücklich bestätigte Positionen werden übertragen. Das Netto aus dem Wareneingang
        // ist ein Vorschlag, kein Nachweis: ohne Häkchen bleibt die Position "fehlt".
        confirmed: !!l.input_tax,
      })));
      setLoaded(true);
    }).catch((e) => toast.error(apiError(e)));
  }, [kind, receiptId, open]);
  useEffect(() => { load(); }, [load]);

  const upd = (i, k, v) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const lines = rows.filter((r) => r.confirmed && r.net !== "" && !Number.isNaN(Number(r.net)))
        .map((r) => ({ index: r.index, tax_code: r.tax_code, net: Number(r.net) }));
      if (lines.length === 0) {
        toast.error(t("itx.noneConfirmed"));
        setBusy(false);
        return;
      }
      const res = await api.put(`/input-tax/${kind}/${receiptId}`, { ...head, lines });
      toast.success(t("itx.saved", { status: t(`itx.status.${res.data.status}`) }));
      onSaved?.(res.data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="input-tax-dialog">
        <DialogHeader><DialogTitle>{t("itx.dialogTitle")}</DialogTitle></DialogHeader>
        <DialogDescription>{t("itx.dialogHint")}</DialogDescription>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label>{t("itx.supplierInvoiceNumber")}</Label>
            <Input className="mt-1.5" value={head.supplier_invoice_number} data-testid="itx-invoice-number"
              disabled={!loaded}
              onChange={(e) => setHead({ ...head, supplier_invoice_number: e.target.value })} /></div>
          <div><Label>{t("itx.documentDate")}</Label>
            <Input type="date" className="mt-1.5" value={head.document_date} data-testid="itx-document-date"
              disabled={!loaded}
              onChange={(e) => setHead({ ...head, document_date: e.target.value })} /></div>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr>
              <Th>{t("common.position")}</Th><Th>{t("itx.confirm")}</Th><Th right>{t("itx.net")}</Th>
              <Th>{t("common.taxRate")}</Th><Th>{t("common.status")}</Th></tr></thead>
            <tbody data-testid="itx-lines">
              {rows.map((r, i) => (
                <tr key={r.index} className="border-t border-border">
                  <Td mono>{r.index + 1}</Td>
                  <Td>
                    <input type="checkbox" className="h-4 w-4" checked={r.confirmed}
                      data-testid={`itx-confirm-${i}`}
                      onChange={(e) => upd(i, "confirmed", e.target.checked)} /></Td>
                  <Td right>
                    <NumberInput decimals={2} value={r.net} testId={`itx-net-${i}`}
                      onChange={(val) => upd(i, "net", val)} /></Td>
                  <Td>
                    <select value={r.tax_code} onChange={(e) => upd(i, "tax_code", e.target.value)}
                      data-testid={`itx-tax-code-${i}`}
                      className="h-9 w-full min-w-[7rem] rounded-md border border-input bg-background px-2 text-sm">
                      {CODES.map((c) => <option key={c} value={c}>{t(`tax.code.${c}`, c)}</option>)}
                    </select></Td>
                  <Td><InputTaxBadge status={r.captured ? "COMPLETE" : "MISSING"}
                    testId={`itx-line-status-${i}`} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data && (
          <div className="rounded-md bg-muted/40 p-3 text-sm" data-testid="itx-summary">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <InputTaxBadge status={data.status} testId="itx-status" />
              <span>{t("itx.net")}: <strong>{eur(data.net)}</strong></span>
              <span>{t("itx.tax")}: <strong>{eur(data.tax)}</strong></span>
              <span>{t("itx.gross")}: <strong>{eur(data.gross)}</strong></span>
              <span className="text-muted-foreground">
                {t("itx.captured", { done: data.lines_captured, total: data.lines_total })}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground" data-testid="itx-split">
              <span>{t("itx.position", "Positionen")}: {eur(data.position_net ?? 0)} → {eur(data.position_tax ?? 0)}</span>
              <span>{t("itx.ancillary", "Nebenkosten")}: {eur(data.ancillary_net ?? 0)} → {eur(data.ancillary_tax ?? 0)}</span>
              <span className="font-medium text-foreground">{t("itx.total", "Gesamt")}: {eur(data.net)} → {eur(data.tax)}</span>
            </div>
            {data.breakdown.length > 0 && (
              <div className="mt-2 space-y-0.5 text-xs text-muted-foreground" data-testid="itx-breakdown">
                {data.breakdown.map((b) => (
                  <div key={`${b.tax_code}-${b.tax_rate}`}>
                    {t(`tax.code.${b.tax_code}`, b.tax_code)} · {b.tax_rate}% · {eur(b.net)} → {eur(b.tax)}
                  </div>
                ))}
              </div>
            )}
            {data.ancillary?.length > 0 && (
              <div className="mt-2 border-t border-border pt-2" data-testid="itx-ancillary">
                <div className="text-xs font-medium text-foreground">{t("itx.ancillary", "Nebenkosten-Vorsteuer")}</div>
                <div className="text-[11px] text-muted-foreground">{t("itx.ancillaryHint", "Aus dem Wareneingang übernommen — nicht hier bearbeitbar.")}</div>
                <div className="mt-1 space-y-0.5 text-xs" data-testid="itx-ancillary-list">
                  {data.ancillary.map((a, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`itx-anc-${a.kind}-${i}`}>
                      <span className="text-foreground">{t(`itx.kind.${a.kind}`, a.label)}</span>
                      <span className="text-muted-foreground">· {a.tax_code ? t(`tax.code.${a.tax_code}`, a.tax_code) : "—"}{a.tax_rate != null ? ` · ${a.tax_rate}%` : ""}</span>
                      <span className="text-muted-foreground">· {eur(a.net)} → {a.tax != null ? eur(a.tax) : "—"}</span>
                      <InputTaxBadge status={a.status} testId={`itx-anc-status-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.history.length > 0 && (
              <button type="button" className="mt-2 text-xs underline" data-testid="itx-history-toggle"
                onClick={() => setShowHistory((v) => !v)}>
                {t("itx.history", { count: data.history.length })}
              </button>
            )}
            {showHistory && (
              <div className="mt-2 space-y-1 text-xs" data-testid="itx-history">
                {data.history.map((h) => (
                  <div key={h.revision}>
                    #{h.revision} · {fmtDate(h.superseded_at)} · {eur(h.net)} / {eur(h.tax)}
                    {h.supplier_invoice_number ? ` · ${h.supplier_invoice_number}` : ""}
                    {h.superseded_by_email ? ` · ${h.superseded_by_email}` : ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div><Label>{t("itx.reason")}</Label>
          <Input className="mt-1.5" value={head.reason} data-testid="itx-reason"
            onChange={(e) => setHead({ ...head, reason: e.target.value })} /></div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="itx-cancel">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={busy || !loaded} data-testid="itx-save">
            <Receipt className="mr-1.5 h-4 w-4" />{t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
